import React, { useEffect, useState, useRef, useMemo } from "react"; 
import api from "../../api/api";
import {
  CircularProgress,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip
} from "@mui/material";
import Swal from "sweetalert2";
import EditIcon from "@mui/icons-material/Edit";
import logo from "./logoo.png";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const PaymentRequestDetails = () => {
  const [requests, setRequests] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acChargedTo, setAcChargedTo] = useState({});
  const [purposeMap, setPurposeMap] = useState({});
  const [showRequest, setShowRequest] = useState({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [filterId, setFilterId] = useState("");
  const [popupOpen, setPopupOpen] = useState(false);
  const [editingRequestId, setEditingRequestId] = useState(null);
  const token = localStorage.getItem("access");
  const pdfRef = useRef();

  // Fetch requests
  const fetchRequests = async () => {
    try {
      const res = await api.get("/payment-requests/", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRequests(res.data || []);

      const initialShow = {};
      (res.data || []).forEach((r) => {
        initialShow[r.id] = true; // show request by default
      });
      setShowRequest(initialShow);
      setLoading(false);
    } catch (err) {
      console.error("Failed to fetch payment requests:", err);
      setLoading(false);
      Swal.fire("Error", "Failed to fetch payment requests.", "error");
    }
  };

  // Fetch vouchers
  const fetchVouchers = async () => {
    try {
      const res = await api.get("/payment-vouchers/", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setVouchers(res.data || []);
    } catch (err) {
      console.error("Failed to fetch vouchers:", err);
    }
  };

  useEffect(() => {
    fetchRequests();
    fetchVouchers();
  }, []);

  // Filtered requests
  const filteredRequests = useMemo(() => {
    if (!filterId) return requests;
    const id = Number(filterId);
    if (Number.isNaN(id)) return requests;
    return requests.filter((r) => r.id === id);
  }, [requests, filterId]);

  // Keep currentIndex valid
  useEffect(() => {
    if (currentIndex >= filteredRequests.length) {
      setCurrentIndex(Math.max(0, filteredRequests.length - 1));
    }
  }, [filteredRequests, currentIndex]);

  // Helper: get voucher for a request
  const getVoucherForRequest = (requestId) => {
    return vouchers.find(v => v.payment_request === requestId);
  };

  // --- PDF download using dom-to-image + jsPDF ---


const handleDownloadPDF = async () => {
  if (!pdfRef.current) return;

  try {
    // Wait for all images to load first
    const imgs = pdfRef.current.querySelectorAll("img");
    await Promise.all(
      Array.from(imgs).map(img => {
        if (img.complete) return Promise.resolve();
        return new Promise((res) => { img.onload = res; img.onerror = res; });
      })
    );

    // Capture the PDF content
    const canvas = await html2canvas(pdfRef.current, {
      scale: 2,          // higher scale = better quality
      useCORS: true,     // load images from backend
      backgroundColor: "#ffffff",
      logging: false,
    });

    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    // Compute the height of the image in PDF scale
    const imgWidth = pageWidth;
    const imgHeight = (canvas.height * pageWidth) / canvas.width;

    if (imgHeight <= pageHeight) {
      // If content fits on one page
      pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);
    } else {
      // Multi-page content
      let heightLeft = imgHeight;
      let position = 0;

      while (heightLeft > 0) {
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
        position -= pageHeight;
        if (heightLeft > 0) pdf.addPage();
      }
    }

    pdf.save(`PaymentRequest_${filteredRequests[currentIndex].id}.pdf`);
  } catch (err) {
    console.error("PDF generation failed:", err);
    Swal.fire("Error", "Failed to generate PDF.", "error");
  }
};


  // --- Voucher handling (same as your original) ---
  const handleCreateVoucher = async (requestId) => {
    const acValue = acChargedTo[requestId];
    const purposeValue = purposeMap[requestId];

    if (!acValue || !purposeValue) {
      Swal.fire("Warning", "Please fill AC Charged To and Purpose before creating voucher.", "warning");
      return;
    }

    try {
      const res = await api.post(
        "/payment-vouchers/",
        { payment_request: requestId, ac_charged_to: acValue, purpose: purposeValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setVouchers((prev) => [...prev, res.data]);
      setShowRequest((prev) => ({ ...prev, [requestId]: false }));
      setPopupOpen(false);
      setEditingRequestId(null);
      Swal.fire("Success", "Voucher created successfully.", "success");
    } catch (err) {
      console.error("Failed to create voucher:", err);
      Swal.fire("Error", "Voucher creation failed.", "error");
    }
  };

  const handleUpdateVoucher = async (requestId) => {
    const acValue = acChargedTo[requestId];
    const purposeValue = purposeMap[requestId];
    const voucher = getVoucherForRequest(requestId);

    if (!voucher) {
      Swal.fire("Error", "Voucher not found for update.", "error");
      return;
    }
    if (!acValue || !purposeValue) {
      Swal.fire("Warning", "Please fill AC Charged To and Purpose.", "warning");
      return;
    }

    try {
      const res = await api.patch(
        `/payment-vouchers/${voucher.id}/`,
        { ac_charged_to: acValue, purpose: purposeValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setVouchers((prev) => prev.map(v => (v.id === voucher.id ? res.data : v)));
      setPopupOpen(false);
      setEditingRequestId(null);
      Swal.fire("Success", "Voucher updated successfully.", "success");
    } catch (err) {
      console.error("Failed to update voucher:", err);
      Swal.fire("Error", "Voucher update failed.", "error");
    }
  };

  const toggleRequestVisibility = (id) => {
    setShowRequest((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const goPrev = () => setCurrentIndex((i) => Math.max(0, i - 1));
  const goNext = () => setCurrentIndex((i) => Math.min(filteredRequests.length - 1, i + 1));

  const openPopupForRequest = (request) => {
    const id = request.id;
    setEditingRequestId(id);
    const voucher = getVoucherForRequest(id);

    setAcChargedTo((prev) => ({ ...prev, [id]: voucher?.ac_charged_to ?? "" }));
    setPurposeMap((prev) => ({ ...prev, [id]: voucher?.purpose ?? "" }));
    setPopupOpen(true);
  };

  if (loading)
    return (
      <div style={{ textAlign: "center", marginTop: "2rem" }}>
        <CircularProgress />
      </div>
    );

  if (!requests.length)
    return <p style={{ textAlign: "center", marginTop: "2rem" }}>No payment requests found.</p>;

  if (!filteredRequests.length)
    return (
      <div style={{ maxWidth: "900px", margin: "2rem auto", padding: "1rem", background: "#fff" }}>
        <div style={{ marginBottom: "1rem", display: "flex", gap: "0.5rem", alignItems: "center" }}>
          <TextField
            label="Filter by Request ID"
            variant="outlined"
            size="small"
            value={filterId}
            onChange={(e) => setFilterId(e.target.value)}
          />
          <Button onClick={() => { setFilterId(""); setCurrentIndex(0); }}>Clear Filter</Button>
        </div>
        <p style={{ textAlign: "center" }}>No requests match the filter.</p>
      </div>
    );

  const currentRequest = filteredRequests[currentIndex];
  const currentVoucher = getVoucherForRequest(currentRequest.id);

  return (
    <div style={{ maxWidth: "900px", margin: "2rem auto", padding: "1rem", background: "#fff" }}>
      {/* Navigation & Filter */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
          <TextField
            label="Filter by Request ID"
            variant="outlined"
            size="small"
            value={filterId}
            onChange={(e) => { setFilterId(e.target.value); setCurrentIndex(0); }}
          />
          <Button onClick={() => { setFilterId(""); setCurrentIndex(0); }}>Clear Filter</Button>

          {currentVoucher ? (
            <>
              <Tooltip title="Edit AC Charged To / Purpose">
                <IconButton onClick={() => openPopupForRequest(currentRequest)}>
                  <EditIcon />
                </IconButton>
              </Tooltip>
              <Button
                variant="outlined"
                onClick={() => toggleRequestVisibility(currentRequest.id)}
              >
                {showRequest[currentRequest.id] ? "View Voucher" : "View Request"}
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              onClick={() => openPopupForRequest(currentRequest)}
              sx={{
                backgroundColor: "#E0F7FA",
                color: "#006064",
                fontWeight: 500,
                '&:hover': { backgroundColor: "#B2EBF2" },
                textTransform: 'none',
                borderRadius: '8px',
                padding: '6px 16px',
              }}
            >
              Create Voucher
            </Button>
          )}
        </div>

        <div style={{ display: "flex", gap: "0.5rem" }}>
          <Button variant="outlined" onClick={goPrev} disabled={currentIndex === 0}>Prev</Button>
          <Button variant="outlined" onClick={goNext} disabled={currentIndex >= filteredRequests.length - 1}>Next</Button>
          <Button variant="contained" color="primary" onClick={handleDownloadPDF}>Download PDF</Button>
        </div>
      </div>

      {/* Popup for AC Charged To & Purpose */}
      <Dialog
        open={popupOpen}
        onClose={(event, reason) => {
          if (reason === "backdropClick") return;
          setPopupOpen(false);
          setEditingRequestId(null);
        }}
      >
        <DialogTitle>{currentVoucher ? "Edit Voucher" : "Create Voucher"}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth style={{ marginTop: "1rem" }}>
            <InputLabel>AC Charged To</InputLabel>
            <Select
              value={acChargedTo[editingRequestId] ?? ""}
              onChange={(e) => setAcChargedTo(prev => ({ ...prev, [editingRequestId]: e.target.value }))}
            >
              <MenuItem value="building_maintenance">Building Maintenance</MenuItem>
              <MenuItem value="utilities">Utilities</MenuItem>
              <MenuItem value="supplies">Supplies</MenuItem>
              <MenuItem value="events">Events</MenuItem>
              <MenuItem value="transportation">Transportation</MenuItem>
              <MenuItem value="miscellaneous">Miscellaneous</MenuItem>
              <MenuItem value="benevolence_gratuity">Benevolence Gratuity</MenuItem>
              <MenuItem value="loan_payment">Loan Payment</MenuItem>
              <MenuItem value="furniture_purchase">Furniture Purchase/Repairs</MenuItem>
              <MenuItem value="administrative_cost">Administrative Cost</MenuItem>
              <MenuItem value="mmc_senior_pastor_support">MMC Senior Pastor Support</MenuItem>
              <MenuItem value="instructional_supplies">Instructional Supplies</MenuItem>
              <MenuItem value="communication">Communication</MenuItem>
              <MenuItem value="office_supplies">Office Supplies</MenuItem>
              <MenuItem value="graduation_program">Graduation Program</MenuItem>
              <MenuItem value="travel_cost">Travel Cost</MenuItem>
              <MenuItem value="ground_maintenance">Ground Maintenance</MenuItem>
              <MenuItem value="purchase">Purchase</MenuItem>
              <MenuItem value="equipment_purchase">Equipment Purchase & Maintenance</MenuItem>
              <MenuItem value="janatorial_supplies">Janatorial Supplies</MenuItem>
              <MenuItem value="utilities_electricity">Utilities Electricity</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Purpose"
            fullWidth
            value={purposeMap[editingRequestId] ?? ""}
            onChange={(e) => setPurposeMap(prev => ({ ...prev, [editingRequestId]: e.target.value }))}
            style={{ marginTop: "1rem" }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setPopupOpen(false); setEditingRequestId(null); }}>Cancel</Button>
          {currentVoucher ? (
            <Button onClick={() => handleUpdateVoucher(editingRequestId)} variant="contained" color="primary">Update Voucher</Button>
          ) : (
            <Button onClick={() => handleCreateVoucher(editingRequestId)} variant="contained" color="secondary">Create Voucher</Button>
          )}
        </DialogActions>
      </Dialog>

      {/* PDF Content */}
      <div ref={pdfRef} style={{ padding: "2rem", background: "#fff" }}>
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "2rem",
            width: "100%",
          }}
        >
          <img src={logo} alt="School Logo" style={{ height: "80px" }} />
          <div style={{ textAlign: "center", flex: 1 }}>
            <h1 style={{ margin: 0 }}>Messiah Mission Institute</h1>
            <p style={{ margin: 0, fontSize: "0.9rem", color: "#555" }}>
              Morris Farm Paynesville Monrovia - Contacts: +231-XXX-XXXX, +231-XXX-XXXX
            </p>
          </div>
          <img src={logo} alt="School Logo" style={{ height: "80px" }} />
        </div>

        {/* Request / Voucher */}
        <div key={currentRequest.id} style={{ border: "1px solid #ddd", borderRadius: "8px", padding: "1.5rem", marginBottom: "2rem", background: "#f9f9f9" }}>
          {currentVoucher && !showRequest[currentRequest.id] ? (
            <>
              {/* Voucher View */}
              <h2 style={{ textAlign: "center" }}>Voucher for Request #{currentRequest.id}</h2>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "1.5rem" }}>
                <div style={{ flex: 1 }}>
                  <p><strong>Submitted by:</strong> {currentRequest.created_by?.username}</p>
                  <p><strong>Pay To:</strong> {currentRequest.pay_to}</p>
                  <p><strong>Total Amount:</strong> {currentRequest.total_amount}</p>
                  <p><strong>Purpose:</strong> {currentVoucher.purpose ?? "�"}</p>
                </div>
                <div style={{ flex: 1 }}>
                  <p><strong>Voucher No:</strong> {currentVoucher.id ?? "�"}</p>
                  <p><strong>Month:</strong> {currentVoucher.month_name ?? "�"}</p>
                  <p><strong>Date Created:</strong> {currentVoucher.voucher_created_at ? new Date(currentVoucher.voucher_created_at).toLocaleDateString() : "�"}</p>
                  <p><strong>Time Created:</strong> {currentVoucher.voucher_created_at ? new Date(currentVoucher.voucher_created_at).toLocaleTimeString() : "�"}</p>
                </div>
              </div>
              <h3 style={{ marginBottom: "0.5rem" }}>Payment Details (summary)</h3>
              <p><strong>Payment Method:</strong> {currentRequest.payment_method}</p>

              <div style={{ display: "flex", justifyContent: "space-between", marginTop: "2rem" }}>
                <div style={{ textAlign: "center", flex: 1 }}>
                  <strong>Received by ________________________</strong><br />
                  (Department Head/Recipient)
                </div>
                <div style={{ textAlign: "center", flex: 1 }}>
                  <strong>Paid by ___________________________</strong><br />
                  Cashier, MMI
                </div>
              </div>

               <div style={{ display: "flex", justifyContent: "space-between", marginTop: "2rem" }}>
                <div>
                  <strong>Fiscal Affairs</strong> <br />
                  Signs <br />
                  {currentRequest.fiscal_signature_url && (
                    <img src={currentRequest.fiscal_signature_url} height={50} />
                  )}
                </div>

                <div style={{ textAlign: "center" }}>
                  <strong>Principal</strong> <br />
                  Approves <br />
                  {currentRequest.principal_signature_url && (
                    <img src={currentRequest.principal_signature_url} height={50} />
                  )}
                </div>

                <div style={{ textAlign: "right" }}>
                  <strong>Gen. Overseer</strong> <br />
                  Attests <br />
                  {currentRequest.attested_signature_url && (
                    <img src={currentRequest.attested_signature_url} height={50} />
                  )}
                </div>
              </div>


            </>
          ) : (
            <>
              {/* Request View */}
              <h2 style={{ textAlign: "center" }}>Request #{currentRequest.id}</h2>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "1.5rem" }}>
                <div style={{ flex: 1 }}>
                  <p><strong>Submitted by:</strong> {currentRequest.created_by?.username}</p>
                  <p><strong>Pay To:</strong> {currentRequest.pay_to}</p>
                  <p><strong>Total Amount:</strong> {currentRequest.total_amount}</p>
                </div>
                <div style={{ flex: 1 }}>
                  <p><strong>Cash Voucher Number:</strong> {currentRequest.id}</p>
                  <p><strong>Requested By:</strong> {currentRequest.prepared_by}</p>
                  <p><strong>Date: </strong> {currentRequest.date}</p>
                </div>
              </div>

              <h3>Payment Details</h3>
              <p><strong>Payment Method:</strong> {currentRequest.payment_method}</p>

              <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "1rem" }}>
                <thead>
                  <tr style={{ background: "#000", color: "#fff" }}>
                    <th style={{ padding: "0.5rem", border: "1px solid #ddd", textAlign: "left" }}>Description</th>
                    <th style={{ padding: "0.5rem", border: "1px solid #ddd", textAlign: "right" }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {currentRequest.details?.map((d, idx) => (
                    <tr key={idx} style={{ background: idx % 2 === 0 ? "#f1f1f1" : "#fff" }}>
                      <td style={{ padding: "0.5rem", border: "1px solid #ddd" }}>{d.description}</td>
                      <td style={{ padding: "0.5rem", border: "1px solid #ddd", textAlign: "right" }}>{d.amount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <p style={{ fontWeight: "bold" }}>Amount in Words: {currentRequest.amount_in_words}</p>

             <div style={{ display: "flex", justifyContent: "space-between", marginTop: "2rem" }}>
                <div>
                  <strong>Fiscal Affairs</strong> <br />
                  Signs <br />
                  {currentRequest.fiscal_signature_url && (
                    <img src={currentRequest.fiscal_signature_url} height={50} />
                  )}
                </div>

                <div style={{ textAlign: "center" }}>
                  <strong>Principal</strong> <br />
                  Approves <br />
                  {currentRequest.principal_signature_url && (
                    <img src={currentRequest.principal_signature_url} height={50} />
                  )}
                </div>

                <div style={{ textAlign: "right" }}>
                  <strong>Gen. Overseer</strong> <br />
                  Attests <br />
                  {currentRequest.attested_signature_url && (
                    <img src={currentRequest.attested_signature_url} height={50} />
                  )}
                </div>
              </div>


            </>
          )}
        </div>

        <div style={{ textAlign: "center", fontSize: "0.9rem", color: "#333" }}>
          Showing {currentIndex + 1} of {filteredRequests.length} (Request ID: {currentRequest.id})
        </div>
      </div>
    </div>
  );
};

export default PaymentRequestDetails;
